import type { Dispatch, Middleware, UnknownAction } from '@reduxjs/toolkit'

import { findSecretField } from './registry'
import type { BrowserStorages, KeyValueStorage } from './storage'
import type { BoundPersistenceEntry, PersistedEnvelope } from './types'

/**
 * Исполнитель реестра хранения: читает записи при старте/входе, пишет
 * изменения с задержкой, стирает записи пользователя при выходе.
 *
 * Пишет ТОЛЬКО то, что выбрали записи реестра (`select`), и только после того,
 * как своя область прочитана: иначе default слайса, записанный раньше чтения,
 * затёр бы сохранённое.
 */
export interface PersistenceControllerOptions<R> {
  entries: readonly BoundPersistenceEntry<R>[]
  storages: BrowserStorages
  /** Пространство ключей: приложение + окружение (`krp:development`) — D-071. */
  namespace: string
  debounceMs?: number
  now?: () => number
  /** Запись отвергнута (секрет в значении, хранилище недоступно) — в журнал. */
  onRejected?: (key: string, reason: string) => void
}

export interface PersistenceController<R> {
  middleware: Middleware<object, R>
  /** Прочитать записи устройства (`scope: 'device'`). Один раз при сборке стора. */
  hydrateDevice(dispatch: Dispatch<UnknownAction>): void
  /** Прочитать записи пользователя после того, как Gateway сказал, кто вошёл. */
  hydrateUser(dispatch: Dispatch<UnknownAction>, userId: string): void
  /** Выход: стереть записи пользователя и больше не писать их до следующего входа. */
  forgetUser(userId: string): void
  /** Записать отложенное сейчас (перед уходом со страницы, в тестах). */
  flush(state: R): void
  storageKey(entry: Pick<BoundPersistenceEntry<R>, 'key' | 'version' | 'scope'>, userId?: string): string
}

const DEFAULT_DEBOUNCE_MS = 300

function isEnvelope(value: unknown): value is PersistedEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const envelope = value as Partial<PersistedEnvelope>
  return (
    typeof envelope.schemaVersion === 'number' &&
    typeof envelope.savedAt === 'string' &&
    typeof envelope.scope === 'object' &&
    envelope.scope !== null &&
    'value' in envelope
  )
}

export function createPersistenceController<R>(options: PersistenceControllerOptions<R>): PersistenceController<R> {
  const { entries, storages, namespace } = options
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS
  const now = options.now ?? Date.now
  const reject = options.onRejected ?? (() => {})

  let deviceHydrated = false
  let userId: string | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let pendingState: R | null = null
  /** Последнее записанное значение по ключу хранилища: пишем только изменения. */
  const written = new Map<string, unknown>()

  const storageOf = (entry: BoundPersistenceEntry<R>): KeyValueStorage =>
    entry.lifetime === 'session' ? storages.session : storages.device

  const prefixOf = (entry: Pick<BoundPersistenceEntry<R>, 'key'>): string => `${namespace}:${entry.key}:v`

  function storageKey(entry: Pick<BoundPersistenceEntry<R>, 'key' | 'version' | 'scope'>, user?: string): string {
    const base = `${prefixOf(entry)}${entry.version}`
    if (entry.scope === 'device') return base
    if (user === undefined || user === '') throw new Error(`Запись «${entry.key}» пользовательская: нужен userId`)
    return `${base}:u:${encodeURIComponent(user)}`
  }

  /** Удалить записи прежних версий формы этого ключа (D-072). */
  function dropStaleVersions(entry: BoundPersistenceEntry<R>): void {
    const storage = storageOf(entry)
    const current = `${prefixOf(entry)}${entry.version}`
    for (const key of storage.keys()) {
      if (key.startsWith(prefixOf(entry)) && key !== current && !key.startsWith(`${current}:`)) storage.remove(key)
    }
  }

  function read(entry: BoundPersistenceEntry<R>, dispatch: Dispatch<UnknownAction>, user?: string): void {
    const storage = storageOf(entry)
    const key = storageKey(entry, user)
    const raw = storage.get(key)
    if (raw === null) return
    const drop = () => storage.remove(key)
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      drop()
      return
    }
    if (!isEnvelope(parsed) || parsed.schemaVersion !== entry.version) {
      drop()
      return
    }
    if (parsed.expiresAt !== undefined && Date.parse(parsed.expiresAt) <= now()) {
      drop()
      return
    }
    if (entry.scope === 'user' && parsed.scope.userId !== user) {
      drop()
      return
    }
    const action = entry.revive(parsed.value)
    if (action === undefined) {
      drop()
      return
    }
    dispatch(action)
  }

  function write(state: R): void {
    for (const entry of entries) {
      if (entry.scope === 'device' && !deviceHydrated) continue
      if (entry.scope === 'user' && userId === null) continue
      const key = storageKey(entry, userId ?? undefined)
      const value = entry.select(state)
      if (written.get(key) === value) continue
      written.set(key, value)
      const secret = findSecretField(value)
      if (secret !== null) {
        reject(entry.key, `поле ${secret} похоже на секрет — запись не сохранена`)
        continue
      }
      const savedAt = now()
      const envelope: PersistedEnvelope = {
        schemaVersion: entry.version,
        savedAt: new Date(savedAt).toISOString(),
        ...(entry.ttlMs !== undefined ? { expiresAt: new Date(savedAt + entry.ttlMs).toISOString() } : {}),
        scope: entry.scope === 'user' && userId !== null ? { userId } : {},
        value,
      }
      let serialized: string
      try {
        serialized = JSON.stringify(envelope)
      } catch {
        reject(entry.key, 'значение не сериализуется')
        continue
      }
      if (!storageOf(entry).set(key, serialized)) reject(entry.key, 'хранилище недоступно или переполнено')
    }
  }

  function flush(state: R): void {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    pendingState = null
    write(state)
  }

  const middleware: Middleware<object, R> = (api) => (next) => (action) => {
    const result = next(action)
    pendingState = api.getState()
    if (timer === null) {
      timer = setTimeout(() => {
        timer = null
        if (pendingState !== null) {
          const state = pendingState
          pendingState = null
          write(state)
        }
      }, debounceMs)
    }
    return result
  }

  return {
    middleware,
    storageKey,
    flush,
    hydrateDevice(dispatch) {
      for (const entry of entries) {
        if (entry.scope !== 'device') continue
        dropStaleVersions(entry)
        read(entry, dispatch)
      }
      deviceHydrated = true
    },
    hydrateUser(dispatch, user) {
      for (const entry of entries) {
        if (entry.scope !== 'user') continue
        dropStaleVersions(entry)
        read(entry, dispatch, user)
      }
      userId = user
    },
    forgetUser(user) {
      for (const entry of entries) {
        if (entry.scope !== 'user') continue
        const key = storageKey(entry, user)
        storageOf(entry).remove(key)
        written.delete(key)
      }
      if (userId === user) userId = null
    },
  }
}
